import { z } from "zod";

/**
 * How a receipt is keyed: how the ledger's screen takes a remittance, and how the AR team
 * treats receipts whose customer is missing or differs from its invoices'. These belong to the
 * ledger and to how the department does business, not to the keying logic, so they live on the
 * agent definition's config (`cashReceiptKeying`) where they can change without a deploy. The
 * installer for each ledger sets them; the defaults are the conservative reading — key the advice
 * exactly as written, and stop on anything that did not match.
 */
export const cashReceiptKeyingConfigSchema = z.object({
  invoiceApplications: z
    .enum(["per_remittance_line", "per_invoice"])
    .default("per_remittance_line")
    .describe(
      "per_remittance_line keys each advice line as its own application; per_invoice adds up every line naming the same invoice into one application, for ledgers that take one line per invoice.",
    ),
  unresolvedRemittanceLines: z
    .enum(["allow", "block"])
    .default("block")
    .describe(
      "Whether advice lines that never matched an AR invoice stop the receipt: allow keys the matched lines and hands the unmatched ones to the procedure, whose steps say what happens to them (e.g. a suspense line); block escalates the receipt.",
    ),
  otherCustomersInvoices: z
    .enum(["allow", "block"])
    .default("block")
    .describe(
      "Whether a receipt may pay invoices that belong to customers other than its own, as when a parent pays for its subsidiaries: allow keys them under the receipt's customer; block escalates the receipt.",
    ),
  customerlessReceipts: z
    .enum(["from_invoices", "block"])
    .default("block")
    .describe(
      "What to do with a receipt that has no customer: from_invoices keys it under the customer with the most invoices on it (ties go to the first on the advice); block escalates it.",
    ),
});

export type CashReceiptKeyingConfig = z.infer<
  typeof cashReceiptKeyingConfigSchema
>;

/** Where the settings live on an agent definition's `config`. */
export const CASH_RECEIPT_KEYING_CONFIG_KEY = "cashReceiptKeying";

/**
 * The cash-receipt keying settings on an agent definition's `config`, defaults filled in. Throws
 * on a malformed value: keying under settings nobody chose is worse than not keying.
 */
export function readCashReceiptKeyingConfig(
  agentConfig: Record<string, unknown> | null | undefined,
): CashReceiptKeyingConfig {
  return cashReceiptKeyingConfigSchema.parse(
    agentConfig?.[CASH_RECEIPT_KEYING_CONFIG_KEY] ?? {},
  );
}
